import { ArrowRight, ArrowLeft } from "lucide-react";
import Logo from "./Logo";
import StepIndicator from "./StepIndicator";
import { Switch } from "./Switch";
import { useState, useEffect } from "react";

export default function StepFourBrand({
  formData,
  handleInputChange,
  handleNextStep,
  handlePrevStep,
  handleCancel,
}) {
  // State to manage the validity of the form fields
  const [isValid, setIsValid] = useState(false);

  // A regex for basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // A function to validate multiple emails separated by commas
  const validateEmails = (emails) => {
    if (!emails) return false;
    const emailList = emails.split(",").map((email) => email.trim());
    return emailList.every((email) => emailRegex.test(email));
  };

  // useEffect to perform validation whenever the formData changes
  useEffect(() => {
    const { agentName, greeting, recordDisclaimer, notificationEmails } = formData;
    const { disclaimerEnabled } = formData.notificationOptions;

    // A flag to check if the record disclaimer field is valid
    const isRecordDisclaimerValid = disclaimerEnabled
      ? recordDisclaimer?.trim() !== ""
      : true; // It's valid if the disclaimer is not enabled

    // Check if all required fields are non-empty and not just whitespace
    const allFieldsValid =
      agentName?.trim() !== "" &&
      greeting?.trim() !== "" &&
      isRecordDisclaimerValid &&
      validateEmails(notificationEmails);
      
    setIsValid(allFieldsValid);
  }, [formData]); // Run this effect whenever formData changes

  const handleToggle = (field, checked) => {
    handleInputChange(`notificationOptions.${field}`, checked);
    if (field === "disclaimerEnabled") {
      // Set or clear the record disclaimer text based on the toggle state
      const defaultDisclaimer = "This call may be recorded for quality assurance purposes.";
      const newDisclaimerValue = checked ? defaultDisclaimer : "";
      handleInputChange("recordDisclaimer", newDisclaimerValue);
    }
  };

  const onNextStep = () => {
    if (isValid) {
      handleNextStep();
    }
  };

  return (
    <div className="min-h-screen flex items-start justify-center pt-1 p-4" style={{ backgroundColor: '#ffffff' }}>
      <div className="bg-white rounded-2xl   p-8 w-full max-w-md">
        <Logo />
        <StepIndicator currentStep={4} />

        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Brand & Notifications
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Agent Name *
            </label>
            <input
              type="text"
              value={formData.agentName}
              onChange={(e) => handleInputChange("agentName", e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all"
              placeholder="Your Agent's Name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Greeting *
            </label>
            <textarea
              value={formData.greeting}
              onChange={(e) => handleInputChange("greeting", e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all min-h-[80px]"
              placeholder="Initial greeting for your customers"
            />
          </div>

          <div className="flex items-center justify-between pt-4">
            <span className="text-sm font-medium text-gray-700">Disclaimer</span>
            <Switch
              checked={formData.notificationOptions.disclaimerEnabled}
              onCheckedChange={(checked) => handleToggle("disclaimerEnabled", checked)}
            />
          </div>
          
          {formData.notificationOptions.disclaimerEnabled && (
            <div className="animate-fadeIn">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Record Disclaimer *
              </label>
              <textarea
                value={formData.recordDisclaimer}
                onChange={(e) => handleInputChange("recordDisclaimer", e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all min-h-[80px]"
                placeholder="e.g., This call may be recorded..."
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notification Emails *
            </label>
            <input
              type="email"
              value={formData.notificationEmails}
              onChange={(e) => handleInputChange("notificationEmails", e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all"
              placeholder="emails@example.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              Separate multiple emails with commas.
            </p>
          </div>

          <div className="pt-4">
            <h3 className="font-semibold text-gray-900">
              Notification Options
            </h3>
            <div className="space-y-3 mt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Email</span>
                <Switch
                  checked={formData.notificationOptions.email}
                  onCheckedChange={(checked) => handleToggle("email", checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">SMS</span>
                <Switch
                  checked={formData.notificationOptions.sms}
                  onCheckedChange={(checked) => handleToggle("sms", checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">WhatsApp</span>
                <Switch
                  checked={formData.notificationOptions.whatsapp}
                  onCheckedChange={(checked) => handleToggle("whatsapp", checked)}
                />
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={onNextStep}
          // The button is disabled if the form is not valid
          disabled={!isValid}
          className="w-full py-4 bg-gradient-to-r from-green-500 to-teal-600 text-white rounded-lg hover:from-green-600 hover:to-teal-700 transition-all flex items-center justify-center space-x-2 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span>Next Step</span>
          <ArrowRight size={20} />
        </button>

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={handlePrevStep}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
          <button
            onClick={handleCancel}
            className="py-3 px-4 text-red-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
